import type { EstimateResponse } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Card, CardBody } from '@fluxora/ui';

import {
  formatEffectiveRateMinor,
  formatMoneyWireToDollars,
  outsideReferenceRangeNoticeKey,
  usageBasisLabelKey,
} from '../services/estimate.service';

/**
 * The headline "your estimated bill" figure (E-SF04): the estimated monthly cost
 * at the premise's usage, the effective ¢/kWh at that usage, and the usage basis.
 *
 * ── THE USAGE-BASIS LINE IS LOAD-BEARING (E-SF19 P0a) ──────────────────────
 * It names the basis the SERVER priced at — the shopper's own declared figure, a
 * real usage read, or the home-size profile — and every source has its own
 * sentence in `estimate.service.usageBasisLabelKey`. It used to describe every
 * non-history estimate as a profile fallback, which told a customer who had just
 * declared 120 kWh/mo that we could not find their usage.
 *
 * ── AC1 — HOME-SIZE PROFILE FALLBACK IS LABELLED ───────────────────────────
 * When the estimate could not use real history (move-in / new construction), the
 * usage-basis line says it is based on a typical home of the chosen size — the
 * customer is never shown a profile figure as if it were their measured usage.
 *
 * Pure presentation: all formatting comes from `estimate.service` (no money math,
 * no logic in this component).
 */
export interface EstimateBillProps {
  readonly estimate: EstimateResponse;
}

export function EstimateBill({ estimate }: EstimateBillProps): JSX.Element {
  const { t } = useTranslation();
  const cost = formatMoneyWireToDollars(estimate.estimatedMonthlyCost);
  const rate = formatEffectiveRateMinor(estimate.effectiveRateAtUsageMinor);
  const outsideRangeKey = outsideReferenceRangeNoticeKey(estimate.usage);
  const usageBasisTestId = `usage-basis-${estimate.usage.source}`;

  return (
    <Card variant="glass" padding="md">
      <CardBody className="flex flex-col gap-2 p-0" data-testid="estimate-bill">
        <span className="text-xs uppercase tracking-wide text-foreground/60">
          {t('shop.estimate.headline.label')}
        </span>
        <span className="text-3xl font-semibold text-foreground">
          {cost}
          <span className="ml-1 text-base font-normal text-foreground/70">
            {t('shop.estimate.headline.perMonth')}
          </span>
        </span>
        <span className="text-sm text-foreground/70">
          {t('shop.estimate.headline.effectiveRate', { rate })}
        </span>
        <span
          className="mt-1 text-xs text-foreground/60"
          // Keyed on the SOURCE itself, not on a profile-vs-everything-else
          // boolean: with three sources a two-way test id would have labelled a
          // DECLARED basis 'history', which is the same conflation the copy fix
          // above exists to remove.
          data-testid={usageBasisTestId}
        >
          {t(usageBasisLabelKey(estimate.usage), { kwh: estimate.usage.monthlyKwh })}
        </span>
        {/* §25.475 publishes three usage levels. Outside them this figure is an
            EXTENSION of the published curve, and the customer is told so rather
            than left to read it as a published price. */}
        {outsideRangeKey === null ? null : (
          <span
            className="text-xs text-foreground/60"
            data-testid="usage-outside-reference-range"
          >
            {t(outsideRangeKey)}
          </span>
        )}
      </CardBody>
    </Card>
  );
}
